import React, { useMemo, useContext, useState, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import PropTypes from 'prop-types';
import moment from 'moment/moment';
import Modal from '../../../commonComponents/modal/Modal';
import ServerSideList from '../../../commonComponents/lists/ServerSideList';
import WalletProp, { WALLET_TYPE } from '../proptype/walletProp';
import translate from '../../../commonComponents/translate';
import {
  newFormatPrice,
  resolveDocumentUrl,
} from '../../../services/utilityFunctions';
import ModalHeader from '../../../commonComponents/modal/ModalHeader';
import {
  WALLET_TRANSACTION_STATUS,
  WALLET_TRANSACTION_TYPE,
} from '../proptype/walletTransactionProp';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import {
  renderInvoiceNumber,
  renderReceiptNumber,
} from '../../../services/numberUtilis';
import AsyncDateRange from '../../../commonComponents/forms/inputs/AsyncDateRange';

const initialState = {
  startDate: null,
  endDate: null,
};

function TransactionsModal({ t, activeWallet, onClose }) {
  const { organization } = useContext(ConfigsContext);
  const { generateInvoiceOnWalletPayouts = false } =
    organization?.settings || {};
  const directSellingSettings = organization?.directSelling;
  const isCrypto = activeWallet?.type === WALLET_TYPE.CRYPTO;

  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
  });

  const handleDateChange = useCallback(
    (startDate, endDate) => {
      // Update filters with the new startDate and endDate
      setFilters({
        startDate,
        endDate,
      });
    },
    [setFilters],
  );

  const columns = useMemo(() => {
    const columnsToShow = [
      {
        field: 'transactionNumber',
        title: t('commissions.transactionsList.id'),
        textAlign: 'left',
        width: 50,
        sortable: false,
      },
      {
        field: 'transactionType',
        title: t('commissions.transactionsList.typeOfOperation'),
        textAlign: 'left',
        width: 100,
        sortable: false,
        template: ({ transactionType }) =>
          t(`commissions.transactionsList.transactionType.${transactionType}`),
      },
      {
        field: 'description',
        title: t('commissions.transactionsList.description'),
        textAlign: 'left',
        width: 250,
        sortable: false,
      },
      {
        field: 'date',
        title: t('commissions.transactionsList.date'),
        textAlign: 'left',
        width: 100,
        sortable: false,
        template: ({ date }) =>
          renderToStaticMarkup(
            <span>{moment(date).utc().format('DD MMM, YYYY')}</span>,
          ),
      },
      {
        field: 'movement',
        title: t('commissions.transactionsList.movement'),
        textAlign: 'left',
        width: 120,
        sortable: false,
        template: ({ userPrice, amount, currencyInfo, transactionType }) => {
          const isNegative = transactionType === WALLET_TRANSACTION_TYPE.PAYOUT;
          const value = isCrypto
            ? `${amount || 0}${currencyInfo?.symbol}`
            : newFormatPrice(
                userPrice?.total || 0,
                true,
                userPrice?.code,
                true,
              );
          return renderToStaticMarkup(
            <span
              style={{ color: `var(--${isNegative ? 'danger' : 'success'})` }}
            >
              {value}
            </span>,
          );
        },
      },
      {
        field: 'balance',
        title: t('commissions.transactionsList.balance'),
        textAlign: 'left',
        width: 120,
        sortable: false,
        template: ({
          userPriceNewBalance,
          newBalance,
          currencyInfo,
          userPrice,
        }) => {
          const value = isCrypto
            ? `${newBalance || 0}${currencyInfo?.symbol}`
            : newFormatPrice(
                userPriceNewBalance?.total || 0,
                true,
                userPriceNewBalance?.code || userPrice?.code,
                true,
              );
          return renderToStaticMarkup(<span>{value}</span>);
        },
      },
      {
        field: 'status',
        title: t('commissions.transactionsList.status'),
        textAlign: 'left',
        width: 100,
        sortable: false,
        template: ({ status }) => {
          const color = {
            [WALLET_TRANSACTION_STATUS.APPROVED]: 'success',
            [WALLET_TRANSACTION_STATUS.PENDING]: 'warning',
            [WALLET_TRANSACTION_STATUS.REJECTED]: 'danger',
          };
          return renderToStaticMarkup(
            <span style={{ color: `var(--${color[status]})` }}>
              {t(`commissions.transactionsList.status.${status}`)}
            </span>,
          );
        },
      },
      {
        field: 'receipt',
        title: t('wallet.receipt.nr'),
        textAlign: 'center',
        template: ({ receipt }) =>
          renderToStaticMarkup(
            receipt?.number ? (
              <div className="download-link">
                <a
                  target="_blank"
                  rel="noreferrer"
                  className={`m-link ${receipt?.link ? '' : 'link-disabled'}`}
                  href={resolveDocumentUrl(receipt?.link) || '#'}
                >
                  <i className="fa fa-download" />
                  {renderReceiptNumber(receipt?.number)}
                </a>
              </div>
            ) : (
              <>N/A</>
            ),
          ),
      },
    ];

    if (generateInvoiceOnWalletPayouts) {
      columnsToShow.push({
        field: 'invoice',
        title: t('invoice.number'),
        textAlign: 'center',
        template: ({ invoice, owner }) =>
          renderToStaticMarkup(
            invoice?.number ? (
              <div className="download-link">
                <a
                  target="_blank"
                  rel="noreferrer"
                  className={`m-link ${invoice?.link ? '' : 'link-disabled'}`}
                  href={resolveDocumentUrl(invoice?.link) || '#'}
                >
                  <i className="fa fa-download" />
                  {renderInvoiceNumber(owner, invoice?.number)}
                </a>
              </div>
            ) : (
              <>N/A</>
            ),
          ),
      });
    }

    return columnsToShow;
  }, []);

  let title = t('commissions.transactionsList.transactions');
  if (filters.startDate) {
    title += `, ${moment(filters.startDate).format('DD MMM YYYY')}`;
  }
  if (
    filters.endDate &&
    filters.startDate &&
    !moment(filters.startDate).isSame(moment(filters.endDate, 'day'))
  ) {
    title += `- ${moment(filters.endDate).utc().format('DD MMM YYYY')}`;
  }
  return (
    <Modal id="transactionsModal" onClose={onClose}>
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        style={{ maxWidth: '90vw', overflowX: 'auto' }}
        role="document"
      >
        <div className="modal-content">
          <ModalHeader title={title} onClose={() => onClose(null)} />
          <div className="modal-body">
            <div className="container-fluid">
              <div className="row">
                <div className="col-6 margin-bottom-20">
                  <AsyncDateRange
                    id="transactionFilterDate"
                    startDate={filters.startDate}
                    endDate={filters.endDate}
                    placeholder={t('commissions.filter_date_placeholder')}
                    onChange={({ startDate, endDate }) => {
                      handleDateChange(startDate, endDate);
                    }}
                    onClear={() => {
                      setFilters(initialState);
                    }}
                    directSellingSettings={directSellingSettings}
                    parentElId="transactionsModal"
                  />
                </div>
                <div className="col-12">
                  <ServerSideList
                    columns={columns}
                    id="transactionsList"
                    dataSourceURL="/table/analytics/wallet/transactions/list"
                    dataSourceHeaders={{
                      'Accept-Version': '1.0.0',
                      'X-Gamification-API': 'Games',
                    }}
                    dataSourceParams={{
                      sort: '-date',
                    }}
                    query={{
                      type: activeWallet?.type,
                      currency: isCrypto
                        ? activeWallet?.currencyInfo?.code
                        : undefined,
                      ...filters,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

TransactionsModal.propTypes = {
  t: PropTypes.func.isRequired,
  activeWallet: PropTypes.shape(WalletProp.propType).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default translate(TransactionsModal);
